import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth, usePermissions } from '@helios/auth-firebase-web';
import {
  qConversationsByVisitSection,
  qVoiceRecordingsByVisitSection,
  qExtractionsByVisitSection,
  conversationIdForRecording,
  legacyConversationId,
  defaultExpandedConversationId,
  formatConversationDateTime,
  insertConversationStmt,
  insertVoiceRecordingStmt,
  updateVoiceRecordingUploadStmt,
  markConversationSummaryAppendedStmt,
  type VisitClinicalConversationRow,
  type VisitVoiceRecordingRow,
  type VisitClinicalExtractionRow,
  type PatientEncounterRow,
} from '@helios/data-access';
import { uploadVoiceRecording } from '../../../lib/upload-to-storage';
import { PermissionGate } from '../../../auth/PermissionGate';
import { PERM } from '../../../auth/permissionMap';
import { useBrandTokens } from '../../../shell/shared';
import { ConversationCard } from './ConversationCard';
import { ConversationRecorder } from './ConversationRecorder';
import type { VttRecording } from '../../../hooks/useVoiceToText';

/* ------------------------------------------------------------------ */
/*  ChiefComplaintVoicePanel (PRD.274 — one recording per conversation) */
/*                                                                    */
/*  Owns the single recording entry point: every completed recording   */
/*  creates a NEW conversation with exactly that recording, then auto-  */
/*  extracts (in the card). Groups the visit's recordings/extractions   */
/*  into collapsible ConversationCards. Reports each conversation's     */
/*  dated summary up to the Chief Complaint managed log.               */
/* ------------------------------------------------------------------ */

const SECTION_KEY = 'chief_complaint';

interface Props {
  encounter: PatientEncounterRow;
  /** Append one dated summary snapshot into the editable Chief Complaint text. */
  onAppendSummary: (dateTime: string, summary: string) => void;
}

interface ConversationVM {
  id: string;
  startedAt: string | null;
  status: string | null;
  summaryAppendedAt: string | null;
  recordings: VisitVoiceRecordingRow[];
  extraction: VisitClinicalExtractionRow | null;
}

export function ChiefComplaintVoicePanel({ encounter, onAppendSummary }: Props) {
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const { canAccess } = usePermissions();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const visitId = encounter.visit_id;
  const brandTokens = useBrandTokens();
  const primaryColor = brandTokens['--color-primary'] || '#2563eb';

  const canCreate = canAccess(PERM.voiceRecordingCreate);
  const canRunExtraction = canAccess(PERM.clinicalExtractionRun);
  const canReview = canAccess(PERM.clinicalExtractionRun);
  const canEditAllergy = canAccess(PERM.patientWorkspaceEdit);
  const canDelete = canAccess(PERM.voiceRecordingDelete);

  const { data: conversations = [] } = usePowerSyncQuery<VisitClinicalConversationRow>(
    qConversationsByVisitSection(visitId, SECTION_KEY),
  );
  const { data: recordings = [] } = usePowerSyncQuery<VisitVoiceRecordingRow>(
    qVoiceRecordingsByVisitSection(visitId, SECTION_KEY),
  );
  const { data: extractions = [] } = usePowerSyncQuery<VisitClinicalExtractionRow>(
    qExtractionsByVisitSection(visitId, SECTION_KEY),
  );

  const [online, setOnline] = useState(
    typeof navigator === 'undefined' || navigator.onLine !== false,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // ── Recording persistence (moved out of the Chief Complaint text area) ──
  const localUrlsRef = useRef<Map<string, string>>(new Map());
  const pendingBlobsRef = useRef<Map<string, { blob: Blob; mimeType: string }>>(new Map());

  const uploadRecording = useCallback(
    async (recordingId: string) => {
      const pending = pendingBlobsRef.current.get(recordingId);
      if (!db || !pending) return;
      const now = () => new Date().toISOString();
      await db.execute(
        ...toArgs(
          updateVoiceRecordingUploadStmt({
            id: recordingId,
            storagePath: null,
            uploadStatus: 'uploading',
            now: now(),
          }),
        ),
      );
      try {
        const result = await uploadVoiceRecording({
          orgId,
          patientId: encounter.patient_id,
          visitId,
          sectionKey: SECTION_KEY,
          recordingId,
          blob: pending.blob,
          mimeType: pending.mimeType,
        });
        await db.execute(
          ...toArgs(
            updateVoiceRecordingUploadStmt({
              id: recordingId,
              storagePath: result.storagePath,
              uploadStatus: 'uploaded',
              now: now(),
            }),
          ),
        );
        pendingBlobsRef.current.delete(recordingId);
      } catch {
        await db.execute(
          ...toArgs(
            updateVoiceRecordingUploadStmt({
              id: recordingId,
              storagePath: null,
              uploadStatus: 'failed',
              now: now(),
            }),
          ),
        );
      }
    },
    [db, orgId, encounter.patient_id, visitId],
  );

  // Every completed recording → a NEW conversation with just that recording.
  const handleRecordingComplete = useCallback(
    async (rec: VttRecording) => {
      if (!db) return;
      const now = new Date().toISOString();
      const conversationId = crypto.randomUUID();
      // Focus the just-recorded conversation: expand it (and collapse others) as
      // soon as it appears in the list, so the clinician sees its extraction.
      setExpandedIds(new Set([conversationId]));
      // Mark it as recorded-this-session so its card may auto-open the allergy
      // review modal (never triggered by plain page load / on-mount extraction).
      setJustRecordedId(conversationId);
      await db.execute(
        ...toArgs(
          insertConversationStmt({
            id: conversationId,
            orgId,
            clinicId: encounter.clinic_id,
            patientId: encounter.patient_id,
            visitId,
            appointmentId: encounter.appointment_id,
            sectionKey: SECTION_KEY,
            title: null,
            createdBy: userId || null,
            now,
          }),
        ),
      );
      const recordingId = crypto.randomUUID();
      localUrlsRef.current.set(recordingId, URL.createObjectURL(rec.blob));
      pendingBlobsRef.current.set(recordingId, { blob: rec.blob, mimeType: rec.mimeType });
      await db.execute(
        ...toArgs(
          insertVoiceRecordingStmt({
            id: recordingId,
            orgId,
            clinicId: encounter.clinic_id,
            patientId: encounter.patient_id,
            visitId,
            encounterId: encounter.id,
            appointmentId: encounter.appointment_id,
            conversationId,
            sectionKey: SECTION_KEY,
            storagePath: null,
            mimeType: rec.mimeType,
            originalFilename: null,
            sizeBytes: rec.blob.size,
            durationMs: rec.durationMs,
            transcript: rec.transcript,
            transcriptLanguage: null,
            transcriptionStatus: rec.transcript ? 'completed' : 'failed',
            uploadStatus: 'pending',
            recordedAt: now,
            recordedBy: userId || null,
            now,
          }),
        ),
      );
      void uploadRecording(recordingId);

      // PRD.283 — the Chief Complaint text field is fed by the voice summary
      // provider (/api/v1/summarize), NOT the extract-clinical summary. Append
      // exactly one dated snapshot now, on recording completion, and stamp the
      // conversation's append-once guard so it is never appended twice. Extract-
      // clinical still runs independently (allergies/symptoms/diagnoses + its own
      // card summary).
      const summary = (rec.summary ?? '').trim();
      if (summary) {
        onAppendSummary(formatConversationDateTime(now), summary);
        await db.execute(
          ...toArgs(markConversationSummaryAppendedStmt({ id: conversationId, now })),
        );
      }
    },
    [
      db,
      orgId,
      userId,
      encounter.clinic_id,
      encounter.patient_id,
      encounter.id,
      encounter.appointment_id,
      visitId,
      uploadRecording,
      onAppendSummary,
    ],
  );

  // ── Group recordings + extractions into conversation view-models ──
  const vms = useMemo<ConversationVM[]>(() => {
    const recByConv = new Map<string, VisitVoiceRecordingRow[]>();
    for (const r of recordings) {
      const cid = conversationIdForRecording(r, visitId);
      const arr = recByConv.get(cid) ?? [];
      arr.push(r);
      recByConv.set(cid, arr);
    }
    for (const arr of recByConv.values()) {
      arr.sort((a, b) => (a.recorded_at ?? '').localeCompare(b.recorded_at ?? ''));
    }

    const extByConv = new Map<string, VisitClinicalExtractionRow>();
    for (const e of extractions) {
      const cid = e.conversation_id ?? legacyConversationId(visitId);
      const cur = extByConv.get(cid);
      if (!cur || (cur.status !== 'completed' && e.status === 'completed')) extByConv.set(cid, e);
    }

    const ids = new Set<string>([
      ...conversations.map((c) => c.id),
      ...recByConv.keys(),
      ...extByConv.keys(),
    ]);
    const rowById = new Map(conversations.map((c) => [c.id, c]));

    const list: ConversationVM[] = [];
    for (const id of ids) {
      const row = rowById.get(id);
      const recs = recByConv.get(id) ?? [];
      list.push({
        id,
        startedAt: row?.started_at ?? recs[0]?.recorded_at ?? null,
        status: row?.status ?? (id.startsWith('legacy_cc_') ? 'completed' : 'active'),
        summaryAppendedAt: row?.summary_appended_at ?? null,
        recordings: recs,
        extraction: extByConv.get(id) ?? null,
      });
    }
    return list.sort(
      (a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? '') || b.id.localeCompare(a.id),
    );
  }, [conversations, recordings, extractions, visitId]);

  // Default expansion: active (or newest) conversation. User toggles persist.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Conversation recorded in this session (if any) — gates its card's automatic
  // allergy-review popup so page load never triggers it.
  const [justRecordedId, setJustRecordedId] = useState<string | null>(null);
  const initedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current || vms.length === 0) return;
    initedRef.current = true;
    const def =
      defaultExpandedConversationId(
        conversations.length > 0
          ? conversations
          : vms.map((v) => ({ id: v.id, status: v.status, started_at: v.startedAt })),
      ) ??
      vms[0]?.id ??
      null;
    if (def) setExpandedIds(new Set([def]));
  }, [vms, conversations]);

  const toggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!canCreate && vms.length === 0) return null;

  return (
    <PermissionGate
      module={PERM.voiceRecordingView.module}
      subModule={PERM.voiceRecordingView.subModule}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-1)',
          marginTop: 'var(--sp-3)',
        }}
      >
        {canCreate && (
          <ConversationRecorder
            onRecordingComplete={(rec) => void handleRecordingComplete(rec)}
            primaryColor={primaryColor}
          />
        )}

        {vms.length === 0 ? (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            No recorded conversations yet.
          </div>
        ) : (
          vms.map((vm) => (
            <ConversationCard
              key={vm.id}
              encounter={encounter}
              conversationId={vm.id}
              conversationStartedAt={vm.startedAt}
              recordings={vm.recordings}
              extraction={vm.extraction}
              expanded={expandedIds.has(vm.id)}
              onToggle={() => toggle(vm.id)}
              online={online}
              justRecorded={vm.id === justRecordedId}
              brandTokens={brandTokens}
              getLocalUrl={(id) => localUrlsRef.current.get(id) ?? null}
              onRetryUpload={(id) => void uploadRecording(id)}
              canRunExtraction={canRunExtraction}
              canReviewAllergies={canReview}
              canEditAllergy={canEditAllergy}
              canDeleteRecording={canDelete}
            />
          ))
        )}
      </div>
    </PermissionGate>
  );
}

/** Spread a `{ sql, params }` statement into `db.execute(sql, params)` args. */
function toArgs(stmt: { sql: string; params: unknown[] }): [string, unknown[]] {
  return [stmt.sql, stmt.params];
}
